import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import machineService from "../../services/machine.service"; // Ensure path is correct

// --- ASYNC THUNKS ---

export const fetchMachines = createAsyncThunk(
  "machines/fetchMachines",
  async (params, { rejectWithValue }) => {
    try {
      const response = await machineService.getMachines(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch machines" },
      );
    }
  },
);

export const fetchMachine = createAsyncThunk(
  "machines/fetchMachine",
  async (id, { rejectWithValue }) => {
    try {
      const response = await machineService.getMachine(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch machine" },
      );
    }
  },
);

export const fetchCategories = createAsyncThunk(
  "machines/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await machineService.getCategories();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch categories" },
      );
    }
  },
);

export const fetchSubCategories = createAsyncThunk(
  "machines/fetchSubCategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await machineService.getSubCategories(categoryId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch sub-categories" },
      );
    }
  },
);

// --- NEW: Create SubCategory Thunk ---
export const createSubCategory = createAsyncThunk(
  "machines/createSubCategory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await machineService.createSubCategory(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create subcategory",
      );
    }
  },
);

// --- SLICE ---

const machineSlice = createSlice({
  name: "machines",
  initialState: {
    machines: [],
    currentMachine: null,
    similarMachines: [],
    categories: [],
    subCategories: [],
    pagination: null,
    loading: false,
    error: null,
    success: false, // Added for form redirects
  },
  reducers: {
    clearCurrentMachine: (state) => {
      state.currentMachine = null;
      state.similarMachines = [];
    },
    clearSubCategories: (state) => {
      state.subCategories = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    // --- NEW: Reset Success Action ---
    resetSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Machines
      .addCase(fetchMachines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.loading = false;
        state.machines = action.payload.machines;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Single Machine
      .addCase(fetchMachine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMachine.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMachine = action.payload.machine;
        state.similarMachines = action.payload.similarMachines || [];
      })
      .addCase(fetchMachine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.categories || action.payload;
      })

      // Fetch SubCategories
      .addCase(fetchSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.subCategories = action.payload.subCategories || action.payload;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // --- NEW: Create SubCategory Handlers ---
      .addCase(createSubCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSubCategory.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createSubCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export all actions including the new resetSuccess
export const {
  clearCurrentMachine,
  clearError,
  clearSubCategories,
  resetSuccess,
} = machineSlice.actions;

export default machineSlice.reducer;
